import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    getBusinessStoreRatingRuns,
    getBusinessStoreRatingSummary,
    getBusinessStoreRatingCompetitors,
    updateBusinessStoreRatingCompetitors,
    importBusinessStoreRatings,
    updateBusinessStoreRatingSettings,
} from "../api/businessStoreRatingsApi";
import { handleAuthError } from "../api/developerApi";
import "./OrganizationPage.css";

const emptySettings = {
    google_drive_folder_id: "",
    source_sheet_name: "TDSheet",
    scheduler_enabled: false,
    import_hour: 9,
    import_minute: 30,
    timezone: "Europe/Kiev",
    period_days: 14,
    refusal_rate: "0.07",
    shipping_late_rate: "0.03",
    unprocessed_rate: "0.07",
    notes: "",
};

const formatError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
        return detail.map((item) => item.msg || JSON.stringify(item)).join("; ");
    }
    return detail || error?.message || fallback;
};

const numberValue = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value, digits = 2) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "0";
    return parsed.toLocaleString("ru-RU", { maximumFractionDigits: digits });
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ru-RU", { timeZone: "Europe/Kiev" });
};

const formatDate = (value) => {
    if (!value) return "—";
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
};

const toPercent = (value) => {
    const parsed = numberValue(value, 0);
    return String(Number((parsed * 100).toFixed(4)));
};

const fromPercent = (value) => {
    const parsed = numberValue(value, 0);
    return String(parsed / 100);
};

const settingsToDraft = (settings) => ({
    ...emptySettings,
    ...settings,
    google_drive_folder_id: settings?.google_drive_folder_id || "",
    source_sheet_name: settings?.source_sheet_name || "TDSheet",
    timezone: settings?.timezone || "Europe/Kiev",
    notes: settings?.notes || "",
});

// A row's three problem metrics (refusal/shipping/unprocessed) are "problematic"
// once their remaining two-week allowance is spent. Shared by the KPI count and
// by the leader-strip's risk card so both use one definition.
const isProblemRow = (row) => (
    numberValue(row.refusal?.two_week_threshold) <= 0
    || numberValue(row.shipping?.two_week_threshold) <= 0
    || numberValue(row.unprocessed?.two_week_threshold) <= 0
);

const statusPillClass = (status) => {
    if (status === "success") return "g";
    if (status === "warning") return "warn";
    if (status === "error") return "r";
    return "off";
};

const kpiStatusModifier = (status) => {
    if (status === "success") return "good";
    if (status === "warning") return "warn";
    if (status === "error") return "bad";
    return "";
};

const StatusBadge = ({ status }) => (
    <span className={`og-pill ${statusPillClass(status)}`}>{status || "—"}</span>
);

const FreshnessBadge = ({ freshness }) => {
    if (!freshness) return <span>—</span>;
    return <span className={`og-pill ${freshness.is_warning ? "warn" : "g"}`}>{freshness.is_warning ? "устарел" : "актуален"}</span>;
};

// og-rating widget: number + 0..5 bar. ≥4.5 default (green), 4.0-4.5 "mid" (amber),
// <4.0 "low" (red) — reference thresholds, no explicit rating threshold exists in the data.
const ratingModifier = (rating) => {
    const value = numberValue(rating);
    if (value < 4) return "low";
    if (value < 4.5) return "mid";
    return "";
};

const ratingBarWidth = (rating) => {
    const value = numberValue(rating);
    const pct = Math.max(0, Math.min(100, (value / 5) * 100));
    return `${pct}%`;
};

const RatingWidget = ({ rating }) => {
    const modifier = ratingModifier(rating);
    return (
        <span className={`og-rating${modifier ? ` ${modifier}` : ""}`}>
            <span className="num">{formatNumber(rating, 2)}</span>
            <span className="bar"><i style={{ width: ratingBarWidth(rating) }} /></span>
        </span>
    );
};

// og-risk-chip: reuses day_threshold/two_week_threshold/fact as-is. Bad = same
// "limit exhausted" condition as isProblemRow. Bar width is the remaining share of
// the period's allowed budget (two_week_threshold + fact reconstructs that budget,
// since two_week_threshold = budget - fact) — a direct read of the existing numbers,
// not a new business rule.
const chipStats = (block) => {
    const remaining = numberValue(block?.two_week_threshold);
    const fact = numberValue(block?.fact);
    const budget = remaining + fact;
    const pct = budget > 0 ? Math.max(0, Math.min(100, (remaining / budget) * 100)) : 0;
    return { remaining, fact, pct, bad: remaining <= 0 };
};

// Отдельное поле "среднем в день": day_threshold = остаток лимита за период,
// делённый на period_days — сколько ещё можно набрать в среднем за день,
// не уходя в минус по порогу. Может быть отрицательным (лимит уже превышен).
const RiskMetric = ({ label, block }) => {
    const { remaining, fact, pct, bad } = chipStats(block);
    const perDay = numberValue(block?.day_threshold);
    return (
        <div className="og-risk-row">
            <div className={`og-risk-chip${bad ? " bad" : ""}`}>
                <span className="lbl">{label}</span>
                <span className="bar"><i style={{ width: `${pct}%` }} /></span>
                <span className="val">{formatNumber(fact, 0)}/{formatNumber(remaining, 1)}</span>
            </div>
            <div className={`og-risk-day${bad ? " bad" : ""}`}>≈{formatNumber(perDay, 2)}/день</div>
        </div>
    );
};

const codesToText = (codes) => (Array.isArray(codes) ? codes.join("\n") : "");

const Field = ({ label, children, className }) => (
    <div className={`og-fld${className ? ` ${className}` : ""}`}>
        <label>{label}</label>
        {children}
    </div>
);

const OgCheck = ({ label, checked, onChange }) => (
    <button type="button" className={checked ? "og-check ck" : "og-check"} onClick={() => onChange && onChange(!checked)}>
        <span className="box">{checked ? "✓" : ""}</span>
        <span>{label}</span>
    </button>
);

const SUB_TABS = [
    { key: "overview", label: "Сводка" },
    { key: "competitors", label: "Конкуренты" },
    { key: "import", label: "Настройки импорта" },
    { key: "history", label: "История импортов" },
];

const BusinessStoreRatingsPage = ({ embedded = false }) => {
    const [summary, setSummary] = useState(null);
    const [runs, setRuns] = useState([]);
    const [settingsDraft, setSettingsDraft] = useState(emptySettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [subTab, setSubTab] = useState("overview");

    const [competitorSummary, setCompetitorSummary] = useState(null);
    const [competitorCodesText, setCompetitorCodesText] = useState("");
    const [competitorLoading, setCompetitorLoading] = useState(false);
    const [competitorSaving, setCompetitorSaving] = useState(false);
    const [competitorError, setCompetitorError] = useState("");
    const [competitorSuccess, setCompetitorSuccess] = useState("");

    const rows = useMemo(() => summary?.rows || [], [summary]);
    const latestDataRun = summary?.latest_data_run || null;
    const latestRun = summary?.latest_run || null;
    const sourceFreshness = summary?.source_freshness || null;
    const periodDays = Number(summary?.settings?.period_days || settingsDraft.period_days || 14);

    const stats = useMemo(() => {
        const problematic = rows.filter(isProblemRow).length;
        const unmatched = rows.filter((row) => row.match_status === "unmatched").length;
        return { problematic, unmatched, total: rows.length };
    }, [rows]);

    const avgRating = useMemo(() => {
        if (!rows.length) return 0;
        const sum = rows.reduce((acc, row) => acc + numberValue(row.source_rating), 0);
        return sum / rows.length;
    }, [rows]);

    const leaderRows = useMemo(() => (
        [...rows].sort((a, b) => numberValue(b.source_rating) - numberValue(a.source_rating)).slice(0, 3)
    ), [rows]);

    const riskRow = useMemo(() => {
        if (!rows.length) return null;
        return rows.find(isProblemRow)
            || [...rows].sort((a, b) => numberValue(a.source_rating) - numberValue(b.source_rating))[0];
    }, [rows]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const [summaryData, runsData] = await Promise.all([
                getBusinessStoreRatingSummary(),
                getBusinessStoreRatingRuns(20),
            ]);
            setSummary(summaryData);
            setSettingsDraft(settingsToDraft(summaryData?.settings));
            setRuns(runsData);
        } catch (loadError) {
            handleAuthError(loadError);
            setError(formatError(loadError, "Не удалось загрузить рейтинг магазинов."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const competitorRows = useMemo(() => competitorSummary?.rows || [], [competitorSummary]);
    const competitorMissingCodes = useMemo(() => competitorSummary?.missing_codes || [], [competitorSummary]);

    const loadCompetitors = useCallback(async () => {
        setCompetitorLoading(true);
        setCompetitorError("");
        try {
            const data = await getBusinessStoreRatingCompetitors();
            setCompetitorSummary(data);
            setCompetitorCodesText(codesToText(data?.codes));
        } catch (loadError) {
            handleAuthError(loadError);
            setCompetitorError(formatError(loadError, "Не удалось загрузить рейтинг конкурентов."));
        } finally {
            setCompetitorLoading(false);
        }
    }, []);

    useEffect(() => {
        if (subTab === "competitors" && !competitorSummary) {
            loadCompetitors();
        }
    }, [subTab, competitorSummary, loadCompetitors]);

    const saveCompetitorCodes = async () => {
        setCompetitorSaving(true);
        setCompetitorError("");
        setCompetitorSuccess("");
        try {
            const codes = competitorCodesText
                .split(/[\s,;]+/)
                .map((item) => item.trim())
                .filter(Boolean);
            const data = await updateBusinessStoreRatingCompetitors(codes);
            setCompetitorSummary(data);
            setCompetitorCodesText(codesToText(data?.codes));
            setCompetitorSuccess(`Сохранено кодов: ${(data?.codes || []).length}. Найдено в данных: ${(data?.rows || []).length}.`);
        } catch (saveError) {
            handleAuthError(saveError);
            setCompetitorError(formatError(saveError, "Не удалось сохранить коды конкурентов."));
        } finally {
            setCompetitorSaving(false);
        }
    };

    const onDraftChange = (key, value) => {
        setSettingsDraft((current) => ({ ...current, [key]: value }));
    };

    const saveSettings = async () => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = {
                ...settingsDraft,
                google_drive_folder_id: settingsDraft.google_drive_folder_id?.trim() || null,
                source_sheet_name: settingsDraft.source_sheet_name?.trim() || "TDSheet",
                timezone: settingsDraft.timezone?.trim() || "Europe/Kiev",
                import_hour: Number(settingsDraft.import_hour),
                import_minute: Number(settingsDraft.import_minute),
                period_days: Number(settingsDraft.period_days),
                refusal_rate: String(settingsDraft.refusal_rate),
                shipping_late_rate: String(settingsDraft.shipping_late_rate),
                unprocessed_rate: String(settingsDraft.unprocessed_rate),
                notes: settingsDraft.notes?.trim() || null,
            };
            const saved = await updateBusinessStoreRatingSettings(payload);
            setSettingsDraft(settingsToDraft(saved));
            setSummary((current) => ({ ...(current || {}), settings: saved }));
            setSuccess("Настройки рейтинга сохранены.");
        } catch (saveError) {
            handleAuthError(saveError);
            setError(formatError(saveError, "Не удалось сохранить настройки рейтинга."));
        } finally {
            setSaving(false);
        }
    };

    const runImport = async () => {
        setImporting(true);
        setError("");
        setSuccess("");
        try {
            const result = await importBusinessStoreRatings();
            setSuccess(`Импорт завершен: ${result.status}. Строк: ${result.rows_calculated_count || 0}.`);
            await loadData();
        } catch (importError) {
            handleAuthError(importError);
            setError(formatError(importError, "Не удалось импортировать рейтинг из Google Drive."));
        } finally {
            setImporting(false);
        }
    };

    const content = (
        <div className="og-detail">
            <div className="og-card og-toolbar">
                <div className="th">
                    <div>
                        <h2>Рейтинг магазинов</h2>
                        <p>Ежедневный импорт одного сводного Excel из Google Drive (берётся самый свежий файл папки) и расчет порогов по магазинам.</p>
                    </div>
                    <div className="acts">
                        <button type="button" className="og-btn og-btn-primary" onClick={runImport} disabled={importing || loading}>
                            {importing ? "Забор..." : "Забрать сейчас"}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="og-msg err">{error}</div>}
            {success && <div className="og-msg ok">{success}</div>}
            {latestRun?.status === "error" && latestRun?.id !== latestDataRun?.id && (
                <div className="og-callout danger">
                    <span className="ic">⚠</span>
                    <div><strong>Последняя попытка импорта завершилась ошибкой.</strong><div>{latestRun.error || latestRun.message || "Показаны данные предыдущего успешного импорта."}</div></div>
                </div>
            )}
            {sourceFreshness && (
                <div className={sourceFreshness.is_warning ? "og-callout warn" : "og-callout good"}>
                    <span className="ic">{sourceFreshness.is_warning ? "⚠" : "✓"}</span>
                    <div>
                        {sourceFreshness.is_warning ? (
                            <>
                                <strong>Внимание: отображается неактуальный рейтинг.</strong>
                                <div>Файл: {sourceFreshness.file_name || "—"}. Изменён: {formatDateTime(sourceFreshness.modified_time)}. Ожидалась дата: {formatDate(sourceFreshness.expected_date)}{sourceFreshness.age_days !== null && sourceFreshness.age_days !== undefined ? `. Давность: ${sourceFreshness.age_days} дн.` : ""}</div>
                            </>
                        ) : (
                            <>
                                <strong>Источник рейтинга актуален.</strong>
                                <div>Данные из файла «{sourceFreshness.file_name || "—"}» от {formatDateTime(sourceFreshness.modified_time)}.</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="og-card" style={{ padding: "14px 14px 0", marginBottom: "18px" }}>
                <div className="og-tabbar">
                    {SUB_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`og-tab${subTab === tab.key ? " active" : ""}`}
                            onClick={() => setSubTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {subTab === "overview" && (
                    <div style={{ padding: "4px 8px 22px" }}>
                        <div className="og-kpi-row">
                            <div className="og-kpi">
                                <span className="v">{stats.total}</span>
                                <span className="l">Магазинов</span>
                            </div>
                            <div className="og-kpi good">
                                <span className="v">{formatNumber(avgRating, 2)}</span>
                                <span className="l">Средний рейтинг сети</span>
                            </div>
                            <div className={`og-kpi${stats.problematic ? " warn" : ""}`}>
                                <span className="v">{stats.problematic}</span>
                                <span className="l">Проблемных строк</span>
                            </div>
                            <div className={`og-kpi${stats.unmatched ? " bad" : ""}`}>
                                <span className="v">{stats.unmatched}</span>
                                <span className="l">Unmatched</span>
                            </div>
                        </div>

                        {(leaderRows.length > 0 || riskRow) && (
                            <div className="og-leader-strip">
                                {leaderRows.map((row, index) => (
                                    <div className="og-leader-card" key={`leader-${row.id}`}>
                                        <span className="rank">{index + 1}</span>
                                        <div className="body">
                                            <div className="nm">{row.store_name || row.source_trade_brand || row.pharmacy_code || "—"}</div>
                                            <div className="sub">{row.city || "—"}</div>
                                        </div>
                                        <div className="r"><RatingWidget rating={row.source_rating} /></div>
                                    </div>
                                ))}
                                {riskRow && (
                                    <div className="og-leader-card risk">
                                        <span className="rank">⚠</span>
                                        <div className="body">
                                            <div className="nm">{riskRow.store_name || riskRow.source_trade_brand || riskRow.pharmacy_code || "—"}</div>
                                            <div className="sub">{riskRow.city || "—"}{isProblemRow(riskRow) ? " · риск лимита" : ""}</div>
                                        </div>
                                        <div className="r"><RatingWidget rating={riskRow.source_rating} /></div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="og-block" style={{ background: "var(--og-surface)" }}>
                            <div className="og-bh">
                                <span className="ic">📊</span>
                                <h4>Рейтинг магазинов сети</h4>
                                <span className="hint">риск-метрики за {periodDays} дней</span>
                                {latestDataRun && <StatusBadge status={latestDataRun.status} />}
                            </div>
                            <div className="og-tbl-wrap">
                                <table className="og-tbl" style={{ minWidth: "1180px" }}>
                                    <thead>
                                        <tr>
                                            <th>Магазин</th>
                                            <th>Предприятие</th>
                                            <th>Город / адрес</th>
                                            <th>Рейтинг</th>
                                            <th>Заказы</th>
                                            <th>Товары</th>
                                            <th>Отказы</th>
                                            <th>Просрочка отправки</th>
                                            <th>Необработано</th>
                                            <th>Match</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={row.id}>
                                                <td>
                                                    <strong>{row.store_name || row.source_trade_brand || "—"}</strong>
                                                    <div className="og-mini">{row.pharmacy_code}</div>
                                                </td>
                                                <td>{row.enterprise_name || row.enterprise_code || row.source_enterprise_code || "—"}</td>
                                                <td>{row.city || "—"}<div className="og-mini">{row.address_ukr || ""}</div></td>
                                                <td><RatingWidget rating={row.source_rating} /></td>
                                                <td>{formatNumber(row.total_orders, 0)}</td>
                                                <td>{formatNumber(row.products_count, 0)}</td>
                                                <td><RiskMetric label="Отказы" block={row.refusal} /></td>
                                                <td><RiskMetric label="Просрочка" block={row.shipping} /></td>
                                                <td><RiskMetric label="Необраб." block={row.unprocessed} /></td>
                                                <td>
                                                    <span className={`og-badge ${row.match_status === "matched" ? "on" : "off"}`}>
                                                        <span className="dot" />{row.match_status || "—"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                        {!rows.length && (
                                            <tr><td colSpan="10">{loading ? "Загрузка..." : "Нет данных рейтинга."}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="og-save-bar notop">
                                <span className="note">Красная шкала риск-метрики — остаток лимита на нуле или ниже за последние {periodDays} дней</span>
                            </div>
                        </div>
                    </div>
                )}

                {subTab === "competitors" && (
                    <div style={{ padding: "4px 8px 22px" }}>
                        <p className="og-mini" style={{ margin: "0 0 14px" }}>
                            Коды аптек-конкурентов из того же сводного файла. Статистика берётся из последнего импорта рейтинга — отдельный забор не нужен.
                        </p>

                        {competitorError && <div className="og-callout danger"><span className="ic">⚠</span><div>{competitorError}</div></div>}
                        {competitorSuccess && <div className="og-callout good"><span className="ic">✓</span><div>{competitorSuccess}</div></div>}

                        <div className="og-block">
                            <div className="og-bh"><span className="ic">🏷</span><h4>Коды конкурентов</h4></div>
                            <Field label="Коды аптек (по одному в строке или через запятую)">
                                <textarea
                                    className="og-inp"
                                    value={competitorCodesText}
                                    onChange={(event) => setCompetitorCodesText(event.target.value)}
                                    placeholder={"59512\n57726\n58021"}
                                />
                            </Field>
                            {competitorMissingCodes.length > 0 && (
                                <div className="og-callout warn" style={{ marginTop: "12px", marginBottom: 0 }}>
                                    <span className="ic">⚠</span>
                                    <div>Нет данных в последнем импорте по кодам: {competitorMissingCodes.join(", ")}</div>
                                </div>
                            )}
                            <div className="og-save-bar notop">
                                <button type="button" className="og-btn og-btn-primary" onClick={saveCompetitorCodes} disabled={competitorSaving || competitorLoading}>
                                    {competitorSaving ? "Сохранение..." : "Сохранить и обновить"}
                                </button>
                                <button type="button" className="og-btn og-btn-ghost" onClick={loadCompetitors} disabled={competitorLoading}>Обновить</button>
                            </div>
                        </div>

                        <div className="og-block" style={{ background: "var(--og-surface)" }}>
                            <div className="og-bh">
                                <span className="ic">📊</span>
                                <h4>Статистика конкурентов</h4>
                                <span className="hint">риск-метрики за {periodDays} дней</span>
                            </div>
                            <div className="og-tbl-wrap">
                                <table className="og-tbl" style={{ minWidth: "1100px" }}>
                                    <thead>
                                        <tr>
                                            <th>Аптека</th>
                                            <th>Город / адрес</th>
                                            <th>Рейтинг</th>
                                            <th>Заказы</th>
                                            <th>Товары</th>
                                            <th>Отказы</th>
                                            <th>Просрочка отправки</th>
                                            <th>Необработано</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {competitorRows.map((row) => (
                                            <tr key={row.id}>
                                                <td>
                                                    <strong>{row.source_trade_brand || row.store_name || "—"}</strong>
                                                    <div className="og-mini">{row.pharmacy_code}</div>
                                                </td>
                                                <td>{row.city || "—"}<div className="og-mini">{row.address_ukr || ""}</div></td>
                                                <td><RatingWidget rating={row.source_rating} /></td>
                                                <td>{formatNumber(row.total_orders, 0)}</td>
                                                <td>{formatNumber(row.products_count, 0)}</td>
                                                <td><RiskMetric label="Отказы" block={row.refusal} /></td>
                                                <td><RiskMetric label="Просрочка" block={row.shipping} /></td>
                                                <td><RiskMetric label="Необраб." block={row.unprocessed} /></td>
                                            </tr>
                                        ))}
                                        {!competitorRows.length && (
                                            <tr><td colSpan="8">{competitorLoading ? "Загрузка..." : "Нет данных. Добавьте коды конкурентов выше."}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {subTab === "import" && (
                    <div style={{ padding: "4px 8px 22px" }}>
                        <div className="og-block">
                            <div className="og-bh"><span className="ic">⚙</span><h4>Источник и расписание</h4></div>
                            <div className="og-grid3">
                                <Field label="Google Drive folder ID" className="og-col-span">
                                    <input className="og-inp" value={settingsDraft.google_drive_folder_id} onChange={(event) => onDraftChange("google_drive_folder_id", event.target.value)} />
                                </Field>
                                <Field label="Лист">
                                    <input className="og-inp" value={settingsDraft.source_sheet_name} onChange={(event) => onDraftChange("source_sheet_name", event.target.value)} />
                                </Field>
                                <Field label="Timezone">
                                    <input className="og-inp" value={settingsDraft.timezone} onChange={(event) => onDraftChange("timezone", event.target.value)} />
                                </Field>
                                <Field label="Период, дней">
                                    <input className="og-inp" type="number" min="1" value={settingsDraft.period_days} onChange={(event) => onDraftChange("period_days", event.target.value)} />
                                </Field>
                                <Field label="Час">
                                    <input className="og-inp" type="number" min="0" max="23" value={settingsDraft.import_hour} onChange={(event) => onDraftChange("import_hour", event.target.value)} />
                                </Field>
                                <Field label="Минута">
                                    <input className="og-inp" type="number" min="0" max="59" value={settingsDraft.import_minute} onChange={(event) => onDraftChange("import_minute", event.target.value)} />
                                </Field>
                                <OgCheck
                                    label="Scheduler включен"
                                    checked={Boolean(settingsDraft.scheduler_enabled)}
                                    onChange={(value) => onDraftChange("scheduler_enabled", value)}
                                />
                            </div>
                        </div>

                        <div className="og-block">
                            <div className="og-bh"><span className="ic">%</span><h4>Ставки лимитов</h4></div>
                            <div className="og-grid3">
                                <Field label="Отказы, %">
                                    <input className="og-inp" type="number" min="0" step="0.1" value={toPercent(settingsDraft.refusal_rate)} onChange={(event) => onDraftChange("refusal_rate", fromPercent(event.target.value))} />
                                </Field>
                                <Field label="Просрочка отправки, %">
                                    <input className="og-inp" type="number" min="0" step="0.1" value={toPercent(settingsDraft.shipping_late_rate)} onChange={(event) => onDraftChange("shipping_late_rate", fromPercent(event.target.value))} />
                                </Field>
                                <Field label="Необработано, %">
                                    <input className="og-inp" type="number" min="0" step="0.1" value={toPercent(settingsDraft.unprocessed_rate)} onChange={(event) => onDraftChange("unprocessed_rate", fromPercent(event.target.value))} />
                                </Field>
                                <Field label="Заметки" className="og-col-span">
                                    <textarea className="og-inp" value={settingsDraft.notes} onChange={(event) => onDraftChange("notes", event.target.value)} />
                                </Field>
                            </div>
                        </div>

                        <div className="og-save-bar">
                            <button type="button" className="og-btn og-btn-primary" onClick={saveSettings} disabled={saving || loading}>{saving ? "Сохранение..." : "Сохранить"}</button>
                            <button type="button" className="og-btn og-btn-ghost" onClick={loadData} disabled={loading}>Обновить</button>
                        </div>

                        <div className="og-block" style={{ marginTop: "18px" }}>
                            <div className="og-bh"><span className="ic">🕘</span><h4>Последний импорт</h4></div>
                            {latestRun ? (
                                <>
                                    <div className="og-kpi-row">
                                        <div className={`og-kpi${kpiStatusModifier(latestRun.status) ? ` ${kpiStatusModifier(latestRun.status)}` : ""}`}>
                                            <span className="v">{latestRun.status || "—"}</span>
                                            <span className="l">Статус</span>
                                        </div>
                                        <div className="og-kpi">
                                            <span className="v">{formatDateTime(latestRun.started_at)}</span>
                                            <span className="l">Запуск</span>
                                        </div>
                                        <div className="og-kpi">
                                            <span className="v">{latestRun.rows_calculated_count || 0}</span>
                                            <span className="l">Строк</span>
                                        </div>
                                        <div className={`og-kpi${latestRun.unmatched_count ? " bad" : ""}`}>
                                            <span className="v">{latestRun.unmatched_count || 0}</span>
                                            <span className="l">Unmatched</span>
                                        </div>
                                    </div>
                                    {latestRun.message && <p className="og-mini">{latestRun.message}</p>}
                                    {latestRun.error && <div className="og-msg err">{latestRun.error}</div>}
                                    <div className="og-tbl-wrap">
                                        <table className="og-tbl">
                                            <thead><tr><th>Файл</th><th>Изменен</th><th>Статус</th><th>Строк</th><th>Дубли</th></tr></thead>
                                            <tbody>
                                                {(latestDataRun?.source_files || latestRun.source_files || []).map((file) => (
                                                    <tr key={file.id}>
                                                        <td>{file.file_name}</td>
                                                        <td>{formatDateTime(file.modified_time)}</td>
                                                        <td><StatusBadge status={file.status} /></td>
                                                        <td>{file.rows_count}</td>
                                                        <td>{file.duplicate_rows_count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : <p className="og-mini">Импортов еще нет.</p>}
                        </div>
                    </div>
                )}

                {subTab === "history" && (
                    <div style={{ padding: "4px 8px 22px" }}>
                        <div className="og-tbl-wrap">
                            <table className="og-tbl">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Время</th>
                                        <th>Trigger</th>
                                        <th>Статус</th>
                                        <th>Актуальность файла</th>
                                        <th>Файлы</th>
                                        <th>Строки</th>
                                        <th>Matched</th>
                                        <th>Unmatched</th>
                                        <th>Сообщение</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map((run) => (
                                        <tr key={run.id}>
                                            <td>{run.id}</td>
                                            <td>{formatDateTime(run.started_at)}</td>
                                            <td>{run.trigger}</td>
                                            <td><StatusBadge status={run.status} /></td>
                                            <td><FreshnessBadge freshness={run.source_freshness} /></td>
                                            <td>{run.files_imported_count}/{run.files_found_count}</td>
                                            <td>{run.rows_calculated_count}</td>
                                            <td>{run.matched_count}</td>
                                            <td>{run.unmatched_count}</td>
                                            <td>{run.message || run.error || "—"}</td>
                                        </tr>
                                    ))}
                                    {!runs.length && <tr><td colSpan="9">История пуста.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    if (embedded) return content;

    return (
        <div className="organization-page" style={{ padding: "24px", maxWidth: "1440px", margin: "0 auto", boxSizing: "border-box" }}>
            {content}
        </div>
    );
};

export default BusinessStoreRatingsPage;
